import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Clock, RotateCcw, BookOpen, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Sentry } from '@/lib/sentry';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/lib/auth';
import { TurnColorWord } from '@/lib/turn-color';
import { getChildSkillProgress } from '@/lib/voice-agent/skill-engine';
import { getFlashcardWordsForBook } from '@/services/flashcardSessionService';
import { recordLearningSession } from '@/services/learningProgressService';
import {
  type FlashcardWord,
  type FlashcardResult,
  type SessionSummary,
  formatSessionTime,
  calculateSessionSummary,
  getIncorrectWords,
  shouldBill,
  shuffleWords,
  FLASHCARD_CONFIG,
} from '@/services/flashcardSessionLogic';

type ViewState = 'loading' | 'error' | 'not-enough-words' | 'session' | 'summary';

export function FlashcardSession() {
  const { id: bookId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const childId = searchParams.get('child');
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [viewState, setViewState] = useState<ViewState>('loading');
  const [words, setWords] = useState<FlashcardWord[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState<FlashcardResult[]>([]);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [summary, setSummary] = useState<SessionSummary | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const startTimeRef = useRef<number>(0);
  const cardStartTimeRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  const bookUrl = `/read/book/${bookId}?child=${childId}`;

  // ── Redirect if required params missing ─────────────────────────────
  useEffect(() => {
    if (!bookId || !childId) {
      navigate(bookId ? `/library/book/${bookId}` : '/', { replace: true });
    }
  }, [bookId, childId, navigate]);

  // ── Load words ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!bookId || !childId) return;

    let cancelled = false;

    async function load() {
      try {
        // Check if child has graduated from Color Program
        const { data: childData } = await supabase
          .from('children')
          .select('in_color_program')
          .eq('id', childId!)
          .maybeSingle();

        let activeSkillIds: string[] = [];
        if (childData?.in_color_program !== false) {
          const progress = await getChildSkillProgress(childId!);
          activeSkillIds = progress
            .filter(p => p.status === 'learning' || p.status === 'mastered')
            .map(p => p.skill_id);
        }

        const flashcardWords = await getFlashcardWordsForBook(bookId!, activeSkillIds);
        if (cancelled) return;

        if (flashcardWords.length < FLASHCARD_CONFIG.MIN_WORDS_TO_START) {
          setViewState('not-enough-words');
          return;
        }

        setWords(shuffleWords(flashcardWords));
        setViewState('session');
        startTimeRef.current = Date.now();
        cardStartTimeRef.current = Date.now();
      } catch (err) {
        if (cancelled) return;
        console.error('[FlashcardSession] load error:', err);
        Sentry.captureException(err, { tags: { flow: 'flashcards', step: 'load_words' } });
        toast.error('Failed to load flashcard words');
        setViewState('error');
      }
    }

    load();
    return () => { cancelled = true; };
  }, [bookId, childId]);

  // ── Timer ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (viewState !== 'session') return;

    timerRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, FLASHCARD_CONFIG.TIMER_INTERVAL_MS);

    return () => clearInterval(timerRef.current);
  }, [viewState]);

  // ── Persist session results ─────────────────────────────────────────
  const persistSession = useCallback(async (
    finalResults: FlashcardResult[],
    sessionSummary: SessionSummary,
  ) => {
    if (!childId || !bookId) return;
    setIsSaving(true);

    try {
      const result = await recordLearningSession({
        childId,
        mode: 'flashcards',
        durationSeconds: sessionSummary.durationSeconds,
        bookId: bookId!,
        totalWords: sessionSummary.total,
        correct: sessionSummary.correct,
        incorrect: sessionSummary.incorrect,
        accuracy: sessionSummary.accuracy,
      });

      if (!result.success || !result.sessionId) {
        console.error('[FlashcardSession] session creation failed:', result.error);
        Sentry.captureException(new Error(result.error || 'Session creation failed'), {
          tags: { flow: 'flashcards', step: 'create_session' },
        });
        toast.error('Could not save session. Your progress was not recorded.');
        return;
      }

      const sessionId = result.sessionId;

      if (sessionId) {
        const { error: detailsError } = await supabase
          .from('flashcard_session_details' as any)
          .insert(
            finalResults.map(r => ({
              session_id: sessionId,
              child_id: childId,
              book_id: bookId,
              word_id: r.wordId,
              word: r.word,
              turn_code: r.turnCode,
              skill_id: r.skillId,
              card_order: r.cardOrder,
              parent_feedback: r.parentFeedback,
              time_on_card_ms: r.timeOnCardMs,
            }))
          );
        if (detailsError) {
          console.error('[FlashcardSession] details insert error:', detailsError.message);
          Sentry.captureException(new Error(detailsError.message), {
            tags: { flow: 'flashcards', step: 'persist_details' },
          });
        }
      }

      const correctResults = finalResults.filter(r => r.parentFeedback === 'correct');
      if (correctResults.length > 0) {
        const now = new Date().toISOString();
        const { error: rpcError } = await supabase.rpc(
          'increment_vocabulary_mastery' as any,
          {
            p_child_id: childId,
            p_entries: JSON.stringify(
              correctResults.map(r => ({ word_id: r.wordId, skill_id: r.skillId }))
            ),
            p_practiced_at: now,
          }
        );
        if (rpcError) {
          // Fallback: batched upsert (doesn't increment total_productions, but preserves data)
          await supabase.from('child_vocabulary_mastery').upsert(
            correctResults.map(r => ({
              child_id: childId,
              word_id: r.wordId,
              skill_id: r.skillId,
              status: 'mastered',
              total_productions: 1,
              last_practiced: now,
            })),
            { onConflict: 'child_id,word_id' },
          );
        }
      }

      if (shouldBill(sessionSummary.durationSeconds) && sessionId) {
        const minutes = Math.ceil(sessionSummary.durationSeconds / 60);
        const billingBody = { childId, minutes, sessionId, bookId };

        let billingError: Error | null = null;
        for (let attempt = 0; attempt < 2; attempt++) {
          const { error } = await supabase.functions.invoke('record-flashcard-usage', {
            body: billingBody,
          });
          if (!error) {
            billingError = null;
            break;
          }
          billingError = error;
          if (attempt === 0) await new Promise(r => setTimeout(r, 1500));
        }
        if (billingError) {
          console.error('[FlashcardSession] billing error after retry:', billingError);
          Sentry.captureException(billingError, {
            tags: { flow: 'flashcards', step: 'record_usage' },
            extra: { childId, minutes, sessionId, bookId },
          });
        }
      }
    } catch (err) {
      console.error('[FlashcardSession] persist error:', err);
      Sentry.captureException(err, { tags: { flow: 'flashcards', step: 'persist_session' } });
      toast.error('Could not save session results. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [childId, bookId]);

  // ── Handle parent feedback ──────────────────────────────────────────
  const handleFeedback = useCallback((feedback: 'correct' | 'incorrect') => {
    const currentWord = words[currentIndex];
    if (!currentWord) return;

    const now = Date.now();
    const result: FlashcardResult = {
      wordId: currentWord.wordId,
      word: currentWord.word,
      turnCode: currentWord.turnCode,
      skillId: currentWord.skillId,
      parentFeedback: feedback,
      timeOnCardMs: now - cardStartTimeRef.current,
      cardOrder: currentIndex,
    };

    const nextResults = [...results, result];
    setResults(nextResults);

    if (currentIndex + 1 >= words.length) {
      clearInterval(timerRef.current);
      const durationSeconds = Math.floor((now - startTimeRef.current) / 1000);
      const sessionSummary = calculateSessionSummary(nextResults, durationSeconds);
      setSummary(sessionSummary);
      setViewState('summary');
      persistSession(nextResults, sessionSummary);
    } else {
      setCurrentIndex(prev => prev + 1);
      cardStartTimeRef.current = now;
    }
  }, [words, currentIndex, results, persistSession]);

  // ── Practice Again (incorrect words only) ───────────────────────────
  const handlePracticeAgain = useCallback(() => {
    const incorrectWords = getIncorrectWords(results);
    if (incorrectWords.length === 0) return;

    setWords(shuffleWords(incorrectWords));
    setCurrentIndex(0);
    setResults([]);
    setSummary(null);
    startTimeRef.current = Date.now();
    cardStartTimeRef.current = Date.now();
    setElapsedSeconds(0);
    setViewState('session');
  }, [results]);

  // ── Loading ─────────────────────────────────────────────────────────
  if (viewState === 'loading') {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-[#5C2D91] animate-spin" />
          <p className="text-[#636366] text-sm">Loading flashcards…</p>
        </div>
      </div>
    );
  }

  // ── Error (network/server failure) ────────────────────────────────
  if (viewState === 'error') {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-6">
        <div className="glass-elevated rounded-2xl p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-orange-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#1C1C1E] mb-2">Something went wrong</h2>
          <p className="text-[#636366] text-sm mb-6">
            We couldn't load the flashcard words. Please check your connection and try again.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate(bookUrl)}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#5C2D91] text-white rounded-xl text-sm font-medium min-h-[44px]"
            >
              <RotateCcw className="w-4 h-4" />
              Try Again
            </button>
            <button
              onClick={() => navigate(bookUrl)}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-black/[0.05] text-[#1C1C1E] rounded-xl text-sm font-medium min-h-[44px]"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Book
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Not enough words ────────────────────────────────────────────────
  if (viewState === 'not-enough-words') {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-6">
        <div className="glass-elevated rounded-2xl p-8 max-w-md w-full text-center">
          <BookOpen className="w-12 h-12 text-[#8E8E93] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#1C1C1E] mb-2">Not enough words</h2>
          <p className="text-[#636366] text-sm mb-6">
            This book doesn't have enough matching words for your child's current skills.
            Keep reading to unlock more words!
          </p>
          <button
            onClick={() => navigate(bookUrl)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#5C2D91] text-white rounded-xl text-sm font-medium min-h-[44px]"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Book
          </button>
        </div>
      </div>
    );
  }

  // ── Summary ─────────────────────────────────────────────────────────
  if (viewState === 'summary' && summary) {
    const accuracyPct = Math.round(summary.accuracy * 100);
    const hasIncorrect = summary.incorrect > 0;

    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-6">
        <div className="glass-elevated rounded-2xl p-8 max-w-md w-full">
          <h2 className="text-2xl font-bold text-[#1C1C1E] text-center mb-6">Session Complete</h2>

          {/* Accuracy ring */}
          <div className="flex justify-center mb-6">
            <div className="w-32 h-32 rounded-full border-8 border-[#5C2D91]/20 flex items-center justify-center relative">
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: `conic-gradient(#5C2D91 ${accuracyPct}%, transparent ${accuracyPct}%)`,
                  mask: 'radial-gradient(farthest-side, transparent calc(100% - 8px), #000 calc(100% - 8px))',
                  WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 8px), #000 calc(100% - 8px))',
                }}
              />
              <span className="text-3xl font-bold text-[#5C2D91]">{accuracyPct}%</span>
            </div>
          </div>

          {/* Stats row */}
          <div className="flex justify-center gap-8 mb-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">{summary.correct}</p>
              <p className="text-xs text-[#8E8E93]">Correct</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-500">{summary.incorrect}</p>
              <p className="text-xs text-[#8E8E93]">Incorrect</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-[#1C1C1E]">{formatSessionTime(summary.durationSeconds)}</p>
              <p className="text-xs text-[#8E8E93]">Duration</p>
            </div>
          </div>

          {/* Skill breakdown */}
          {Object.keys(summary.skillBreakdown).length > 0 && (
            <div className="border-t border-black/[0.06] pt-4 mb-6">
              <p className="text-xs font-medium text-[#8E8E93] uppercase tracking-wider mb-3">By Skill</p>
              <div className="space-y-2">
                {Object.entries(summary.skillBreakdown).map(([skillId, counts]) => {
                  const skillTotal = counts.correct + counts.incorrect;
                  const skillAcc = skillTotal > 0 ? Math.round((counts.correct / skillTotal) * 100) : 0;
                  return (
                    <div key={skillId} className="flex items-center justify-between text-sm">
                      <span className="text-[#636366] truncate mr-2">{skillId}</span>
                      <span className="text-[#1C1C1E] font-medium whitespace-nowrap">
                        {counts.correct}/{skillTotal} ({skillAcc}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {isSaving && (
            <p className="text-center text-xs text-[#8E8E93] mb-4">Saving results…</p>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3">
            {hasIncorrect && (
              <button
                onClick={handlePracticeAgain}
                disabled={isSaving}
                className="flex items-center justify-center gap-2 w-full py-3 bg-[#5C2D91] text-white rounded-xl text-sm font-medium min-h-[44px] disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4" />
                Practice Again ({summary.incorrect} words)
              </button>
            )}
            <button
              onClick={() => navigate(bookUrl)}
              className={`flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-medium min-h-[44px] ${
                hasIncorrect
                  ? 'bg-black/[0.05] text-[#1C1C1E]'
                  : 'bg-[#5C2D91] text-white'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Active session ──────────────────────────────────────────────────
  const currentWord = words[currentIndex];
  if (!currentWord) {
    navigate(bookUrl, { replace: true });
    return null;
  }

  return (
    <div className="min-h-[100dvh] bg-[#FAF6F0] flex flex-col">
      {/* Header bar */}
      <div className="flex items-center justify-between px-5 py-3">
        <button
          onClick={() => navigate(bookUrl)}
          className="flex items-center gap-1.5 text-[#636366] text-sm min-h-[44px] min-w-[44px]"
          aria-label="Back to book"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="hidden sm:inline">Back</span>
        </button>

        <p className="text-[#8E8E93] text-sm font-medium tabular-nums">
          {currentIndex + 1} / {words.length}
        </p>

        <div className="flex items-center gap-1.5 text-[#636366] text-sm min-h-[44px]">
          <Clock className="w-4 h-4" />
          <span className="tabular-nums">{formatSessionTime(elapsedSeconds)}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-5">
        <div className="h-1.5 bg-black/[0.06] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#5C2D91] rounded-full transition-all duration-500 ease-out"
            style={{ width: `${((currentIndex) / words.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Word card — canonical Turn Color rendering */}
      <div className="flex-1 flex items-center justify-center px-6 py-8">
        <div
          key={currentWord.wordId}
          className="glass-elevated rounded-3xl px-12 py-10 sm:px-16 sm:py-14 min-w-[200px] max-w-[90vw] text-center animate-[fadeIn_0.2s_ease-out]"
        >
          <div className="turn-color-text" style={{ fontSize: 'clamp(48px, 12vw, 96px)', lineHeight: 1.3 }}>
            <TurnColorWord
              word={currentWord.word}
              turnCode={currentWord.turnCode}
              phase={2}
            />
          </div>
        </div>
      </div>

      {/* Feedback buttons */}
      <div className="px-5 pb-8 pt-2">
        <div className="flex gap-4 max-w-lg mx-auto">
          <button
            onClick={() => handleFeedback('incorrect')}
            className="flex-1 flex items-center justify-center gap-2.5 py-5 bg-orange-400 hover:bg-orange-500 text-white rounded-2xl text-lg font-bold min-h-[64px] active:scale-[0.96] transition-all shadow-lg shadow-orange-400/25"
          >
            <XCircle className="w-7 h-7" />
            Incorrect
          </button>
          <button
            onClick={() => handleFeedback('correct')}
            className="flex-1 flex items-center justify-center gap-2.5 py-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl text-lg font-bold min-h-[64px] active:scale-[0.96] transition-all shadow-lg shadow-emerald-500/25"
          >
            <CheckCircle className="w-7 h-7" />
            Correct
          </button>
        </div>
      </div>
    </div>
  );
}
